import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getClientsByRegion } from '@/shared/api/base-api';
import { Client } from '@/types/base';
import { useUser } from '@/shared/hooks/useUser';
import { BaseLayout } from '@/components/layout';
import { Button } from '@/components/ui/button';

const AllClients: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [activeRegion, setActiveRegion] = useState<'Denver' | 'Austin'>('Denver');

  // Fetch clients by region
  const { data: clientsData, isLoading, error } = useQuery<{ clients: Client[] }>({
    queryKey: ['clients', activeRegion],
    queryFn: () => getClientsByRegion(activeRegion),
  });

  const clients = clientsData?.clients || [];

  const handleClientClick = (clientName: string) => {
    navigate(`/app/dashboard/${clientName}`);
  };

  const getClientLogo = (client: Client) => {
    // Prioritize SVG logo URL (from serializer)
    if (client.logo_svg_url) {
      return { type: 'svg', url: client.logo_svg_url };
    }
    
    // Check for light and dark logo URLs
    if (client.logo_light_url && client.logo_dark_url) {
      return { type: 'dual', lightUrl: client.logo_light_url, darkUrl: client.logo_dark_url };
    }
    
    // Just light logo URL
    if (client.logo_light_url) {
      return { type: 'single', url: client.logo_light_url };
    }
    
    // Fallback to direct fields (backward compatibility)
    if (client.logo_svg) {
      return { type: 'svg', url: client.logo_svg };
    }
    
    if (client.logo_light && client.logo_dark) {
      return { type: 'dual', lightUrl: client.logo_light, darkUrl: client.logo_dark };
    }
    
    if (client.logo_light) {
      return { type: 'single', url: client.logo_light };
    }
    
    // Fallback
    return { type: 'fallback', url: '/static/images/iv360-p-500.png' };
  };

  if (!user?.is_staff) {
    return (
      <BaseLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">All Clients</h1>
            <p className="mt-2 text-muted-foreground">Browse and manage all clients across regions</p>
          </div>

          {/* Region Toggle */}
          <div className="mb-8 flex justify-center">
            <div className="inline-flex h-10 items-center bg-muted border border-border rounded-lg p-1 gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveRegion('Denver')}
                className={`rounded-md px-4 font-medium transition-colors ${
                  activeRegion === 'Denver'
                    ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                Denver
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveRegion('Austin')}
                className={`rounded-md px-4 font-medium transition-colors ${
                  activeRegion === 'Austin'
                    ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                Austin
              </Button>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-12">
              <p className="text-destructive">Error loading clients. Please try again.</p>
            </div>
          )}

          {/* Clients Grid */}
          {!isLoading && !error && (
            <>
              {clients.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {clients.map((client) => {
                    const logoInfo = getClientLogo(client);
                    
                    return (
                      <div
                        key={client.id}
                        onClick={() => handleClientClick(client.name)}
                        className="bg-card rounded-lg border border-border shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden group"
                      >
                        {/* Client Logo Container */}
                        <div className="aspect-[3/2] overflow-hidden bg-card flex items-center justify-center p-6">
                          {logoInfo.type === 'dual' ? (
                            <>
                              <img
                                src={logoInfo.lightUrl}
                                alt={client.name}
                                className="max-w-full max-h-full object-contain dark:hidden group-hover:scale-105 transition-transform duration-200"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = '/static/images/iv360-p-500.png';
                                }}
                              />
                              <img
                                src={logoInfo.darkUrl}
                                alt={client.name}
                                className="max-w-full max-h-full object-contain hidden dark:block group-hover:scale-105 transition-transform duration-200"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = '/static/images/iv360-p-500.png';
                                }}
                              />
                            </>
                          ) : (
                            <img
                              src={logoInfo.url || logoInfo.lightUrl}
                              alt={client.name}
                              className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-200"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/static/images/iv360-p-500.png';
                              }}
                            />
                          )}
                        </div>

                        {/* Client Info */}
                        <div className="p-4 border-t border-border">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-center">
                            {client.name}
                          </h3>
                          {client.hq_address && (
                            <p className="mt-1 text-sm text-muted-foreground text-center line-clamp-1">
                              {client.hq_address}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-foreground">No clients found</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    No clients available in the {activeRegion} region
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};

export default AllClients;

